import PropTypes from "prop-types";
import React from "react";
import { useFormContext } from "react-hook-form";

function InputField(props) {
  const {
    name,
    label,
    type,
    validation,
    placeholder,
    disabled,
    defaultValue,
    pattern,
    minValue,
  } = props;
  const {
    register,
    formState: { errors },
    setValue,
  } = useFormContext();

  //  Block value less than minValue
  const handleInput = (e) => {
    const value = e.target.value;
    if (value === "") return; // Allow empty temporarily (optional)
    if (Number(value) < minValue) {
      e.target.value = minValue;
      setValue(name, minValue); // sync with form
    }
  };

  // Handle phone number input - only allow digits and limit to 10 characters
  const handlePhoneInput = (e) => {
    let value = e.target.value.replace(/\D/g, ''); // Remove non-digits
    if (value.length > 10) {
      value = value.slice(0, 10); // Limit to 10 digits
    }
    e.target.value = value;
    setValue(name, value); // sync with form
  };

  // Handle version input - only allow digits and dots in x.x.x format
  const handleVersionInput = (e) => {
    let value = e.target.value;
    
    // Remove any character that's not a digit or dot
    value = value.replace(/[^\d.]/g, '');
    
    // Prevent starting with a dot
    if (value.startsWith('.')) {
      value = value.substring(1);
    }
    
    // Split by dots and limit to 3 parts
    const parts = value.split('.');
    if (parts.length > 3) {
      // If more than 3 parts, join first 3 parts
      value = parts.slice(0, 3).join('.');
    }
    
    // Remove consecutive dots
    value = value.replace(/\.{2,}/g, '.');
    
    // Remove trailing dot if it's the only dot or if there are already 2 dots
    const dotCount = (value.match(/\./g) || []).length;
    if (value.endsWith('.') && dotCount > 2) {
      value = value.slice(0, -1);
    }
    
    e.target.value = value;
    setValue(name, value);
  };

  // Get validation rules based on type
  const getValidationRules = () => {
    if (type === "phone") {
      return {
        ...validation,
        pattern: {
          value: /^[6-9]\d{9}$/,
          message: "Please enter a valid 10-digit Indian mobile number"
        },
        minLength: {
          value: 10,
          message: "Phone number must be 10 digits"
        },
        maxLength: {
          value: 10,
          message: "Phone number must be 10 digits"
        }
      };
    }
    if (type === "version") {
      return {
        ...validation,
        pattern: {
          value: /^\d+\.\d+\.\d+$/,
          message: "Please enter version in format x.x.x (e.g., 1.1.1)"
        }
      };
    }
    return validation;
  };

  return (
    <div>
      {label && (
        <label htmlFor={name} className="form-label">
          {label}
          {validation?.required && (
            <span style={{ color: "red" }} className="ms-1">
              *
            </span>
          )}
        </label>
      )}

      {type === "price" ? (
        <div className="input-group">
          <span className="input-group-text bg-primary">₹</span>
          <input
            id={name}
            name={name}
            type="number"
            placeholder={placeholder}
            disabled={disabled}
            className={`form-control ${errors?.[name] ? "is-invalid" : ""}`}
            {...register(name, validation)}
            defaultValue={defaultValue}
            pattern={pattern}
            min={minValue}
            onInput={handleInput}
          />
        </div>
      ) : type === "phone" ? (
        <input
          id={name}
          name={name}
          type="tel"
          placeholder={placeholder || "+91 Enter 10-digit mobile number"}
          disabled={disabled}
          className={`form-control ${errors?.[name] ? "is-invalid" : ""}`}
          {...register(name, getValidationRules())}
          defaultValue={defaultValue}
          maxLength="10"
          onInput={handlePhoneInput}
        />
      ) : type === "version" ? (
        <input
          id={name}
          name={name}
          type="text"
          placeholder={placeholder || "1.0.0"}
          disabled={disabled}
          className={`form-control ${errors?.[name] ? "is-invalid" : ""}`}
          {...register(name, getValidationRules())}
          defaultValue={defaultValue}
          onInput={handleVersionInput}
        />
      ) : (
        <input
          id={name}
          name={name}
          type={type}
          placeholder={placeholder}
          disabled={disabled}
          className={`form-control ${errors?.[name] ? "is-invalid" : ""}`}
          {...register(name, validation)}
          defaultValue={defaultValue}
          pattern={pattern}
          min={minValue}
          onInput={handleInput}
        />
      )}
      {errors !== undefined && errors[name] && (
        <div className="invalid-feedback">
          {errors !== undefined && errors[name].message}
        </div>
      )}
    </div>
  );
}

InputField.propTypes = {
  name: PropTypes.node,
  label: PropTypes.node,
  type: PropTypes.node,
  validation: PropTypes.node,
  placeholder: PropTypes.node,
  disabled: PropTypes.node,
  defaultValue: PropTypes.node,
  pattern: PropTypes.string,
  minValue: PropTypes.number,
};

export default InputField;
